import request, {AxiosPromise, CancelTokenSource} from 'axios';

import {ActionType, EmptyActionType} from '@typescript/common/actionTypes';
import {WindowProductType} from '@typescript/productTypes';
import {PRODUCT} from 'constants/actions';
import {ENDPOINTS} from 'constants/endpoints';
import {instanceGusaBackend} from 'libs/api';

import {
    AddToCartOldType,
    DiscountPriceResponseType,
} from 'reducers/product/discountPrice/discountPriceType';

export {getProductWizard} from './wizard';

let discountPriceCancelSource: CancelTokenSource | null = null;

/**
 * Get current product data
 * @param {number} id - Product id
 * @param {string} promoCode - Promo code
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getProduct(id: number, promoCode?: string): ActionType<AxiosPromise> {
    return {
        type: PRODUCT.GET_PRODUCT,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.PRODUCT_GET_ELASTIC}/${id}${promoCode ? `/${promoCode}` : ''}`,
            params: {eligibleForHto: true},
        }),
    };
}

/**
 * It returns total price with applied coupon
 * @param {number} productId - id
 * @param {AddToCartOldType} data - which are need for correct query
 * @return {ActionType<AxiosPromise>} - action
 */
export function fetchDiscountPrice(
    productId: number,
    data: AddToCartOldType,
): ActionType<AxiosPromise<DiscountPriceResponseType>> | ActionType<Promise<void>> {
    if (discountPriceCancelSource) {
        discountPriceCancelSource.cancel();
    }

    // Cancels request if the previous one is not resolved
    discountPriceCancelSource = request.CancelToken.source();

    return {
        type: PRODUCT.GET_DISCOUNT_PRICE,
        payload: instanceGusaBackend({
            cancelToken: discountPriceCancelSource.token,
            method: 'get',
            url: ENDPOINTS.PRODUCT_GET_DISCOUNT_PRICE({productId}),
            params: data,
        }).catch(error => {
            if (!request.isCancel(error)) {
                throw error;
            }
        }),
    } as ActionType<AxiosPromise<DiscountPriceResponseType>> | ActionType<Promise<void>>;
}

/**
 * Set id for active product
 * @param {number|string} id - Id product
 * @returns {ActionType<number>} - Action
 */
export function setActiveProductId(id: number | string): ActionType<number> {
    return {
        type: PRODUCT.SET_ACTIVE_PRODUCT_ID,
        payload: Number(id),
    };
}

/**
 * Check if the id of product is in My Fit
 * @param {number|string} id - Id product
 * @returns {ActionType<number>} - Action
 */
export function checkIsProductIdInMyFit(id: number | string): ActionType<number> {
    return {
        type: PRODUCT.CHECK_IS_PRODUCT_ID_IN_MY_FIT,
        payload: Number(id),
    };
}

// TODO Rename it from getUpsell to fetchRecentlyViewedItems
/**
 * Get data for recently viewed
 * @param {string} ids - id upsell
 * @returns {ActionType<AxiosPromise>} - Action
 */
export function getUpsell(ids: string | number): ActionType<AxiosPromise> {
    return {
        type: PRODUCT.GET_UPSELL,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.UPSELL_GET_BY_IDS}/${ids}`,
        }),
    };
}

/**
 * Create action for reset quote item to initial state
 * @returns {EmptyActionType} - Create action
 */
export function resetQuoteItem(): EmptyActionType {
    return {
        type: PRODUCT.RESET_QUOTE_ITEM,
    };
}

/**
 * Create action for reset all steps on PDP
 * @returns {EmptyActionType} - Create action
 */
export function resetAllSteps(): EmptyActionType {
    return {
        type: PRODUCT.RESET_ALL_STEPS,
    };
}

/**
 * Set product form errors
 * @param {boolean} isError - no readers power error which doesn't allow to submit
 * @returns {ActionType<boolean>} - action
 */
export function setNoReadersPowerError(isError: boolean): ActionType<boolean> {
    return {
        type: PRODUCT.SET_NO_READERS_POWER_ERROR,
        payload: isError,
    };
}

/**
 * Set product readers power
 * @param {string} power - selected power
 * @returns {ActionType<string>} - action
 */
export function setProductReadersPower(power: string): ActionType<string> {
    return {
        type: PRODUCT.SET_READERS_POWER,
        payload: power,
    };
}

/**
 * Set active product
 * @param {WindowProductType} windowProduct Product id
 * @returns {ActionType<WindowProductType>} Action
 */
export function setActiveWindowProduct(
    windowProduct: WindowProductType,
): ActionType<WindowProductType> {
    return {
        type: PRODUCT.SET_PRODUCT,
        payload: windowProduct,
    };
}

/**
 * Creates clear product action
 *
 * @returns {EmptyActionType} action
 */
export const clearProduct = (): EmptyActionType => ({
    type: PRODUCT.CLEAR_PRODUCT,
});

/**
 * Creates open product action
 *
 * @returns {EmptyActionType} action
 */
export const openProduct = (): EmptyActionType => ({type: PRODUCT.OPENED});
